import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useInventory } from "./InventoryContext";
import { useAuth } from "./AuthContext";

const PurchaseOrderContext = createContext();
export const usePurchaseOrder = () => useContext(PurchaseOrderContext);

export const PurchaseOrderProvider = ({ children }) => {
  const { addStock } = useInventory(); 
  const { user } = useAuth();

  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  // ==================== 1. FETCHING DATA ====================
  const fetchPRs = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await api.get('/purchase/requests');
      setPurchaseRequests(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (err) { console.error("Error fetching PRs"); }
  }, [user]);

  const fetchPOs = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await api.get('/purchase/orders');
      setPurchaseOrders(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (err) { console.error("Error fetching POs"); }
  }, [user]);

  // 🟢 Deep-link single fetch function for notifications & detail views
  const fetchPOById = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/purchase/orders/${id}`);
      const data = res.data?.data || res.data;
      setPurchaseOrders(prev => {
          const exists = prev.find(p => String(p.id) === String(id));
          if (exists) return prev.map(p => String(p.id) === String(id) ? data : p);
          return [data, ...prev];
      });
      return data;
    } catch (err) {
      console.error("Failed to fetch single PO", err);
      return null;
    }
  }, []);

  const fetchPRById = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/purchase/requests/${id}`);
      return res.data?.data || res.data;
    } catch (err) {
      console.error("Failed to fetch PR by ID", err);
      return null;
    }
  }, []);

  useEffect(() => { 
    if (!user || !user.id) {
        setPurchaseRequests([]);
        setPurchaseOrders([]);
    }
  }, [user]);

  // ==================== 2. PURCHASE REQUESTS ====================
  const getPR = (id) => purchaseRequests.find(pr => String(pr.id) === String(id));

  const createPurchaseRequest = async (formData) => {
    const prNumber = `PR-${Date.now().toString().slice(-6)}`;
    const payload = { ...formData, prNumber, lines: formData.lines || [] };
    try {
        const res = await api.post('/purchase/requests', payload);
        fetchPRs();
        return { ...payload, id: res.data?.id || res.data?.data?.id };
    } catch (error) { toast.error("Failed to create PR"); }
  };

  const updatePRStatus = async (id, status) => {
    try {
        await api.put(`/purchase/requests/${id}/status`, { status });
        fetchPRs();
    } catch (error) { toast.error("Failed to update status"); }
  };

  const approvePR = (id) => updatePRStatus(id, "APPROVED");

  const deletePR = async (id) => {
      try { await api.delete(`/purchase/requests/${id}`); fetchPRs(); } catch (e) { toast.error("Failed to delete"); }
  };

  // ==================== 3. QUOTATIONS & PORTAL ====================
  const loadQuotations = async (prId) => {
      try {
          const res = await api.get(`/purchase/requests/${prId}/quotes`);
          const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          setQuotations(prev => [...prev.filter(q => String(q.prId) !== String(prId)), ...data]);
      } catch (error) { console.error(error); }
  };

  const getQuotationsForPR = (prId) => quotations.filter(q => String(q.prId) === String(prId));
  const getSelectedQuotation = (prId) => quotations.find(q => String(q.prId) === String(prId) && q.isSelected);

  const addQuotation = async (prId, data) => {
      try {
          await api.post('/purchase/quotes', { prId, ...data });
          loadQuotations(prId);
          return true;
      } catch (error) { toast.error("Failed to add quote"); return false; }
  };

  const selectQuotation = async (quotationId) => {
      const quote = quotations.find(q => String(q.id) === String(quotationId));
      if(!quote) return;
      try {
          await api.put(`/purchase/quotes/${quotationId}/select`, { prId: quote.prId });
          loadQuotations(quote.prId);
      } catch (error) { toast.error("Failed to select quote"); }
  };

  const getRFQsForVendor = async (vendorId) => {
      try {
        const res = await api.get(`/purchase/portal/${vendorId}/rfqs`);
        return Array.isArray(res.data) ? res.data : (res.data?.data || []);
      } catch { return []; }
  };

  const submitQuoteFromRFQ = async (quoteId, price) => {
      try { await api.post(`/purchase/portal/quotes/${quoteId}/submit`, { price }); return true; } catch { return false; }
  };

  // ==================== 4. PURCHASE ORDERS & WORKFLOW ====================
  const getPO = (id) => purchaseOrders.find(po => String(po.id) === String(id));

  const createPurchaseOrder = async (prId, quoteId) => {
      try {
          await api.post('/purchase/orders/create-from-quote', { prId, quoteId });
          toast.success("PO Created Successfully!");
          fetchPOs(); fetchPRs();
      } catch (error) { toast.error("Failed to create PO"); }
  };

  const updatePOStatus = async (poId, status) => {
      try {
          await api.put(`/purchase/orders/${poId}/status`, { status });
          setPurchaseOrders(prev => prev.map(po => String(po.id) === String(poId) ? { ...po, status } : po));
          fetchPOs(); 
          return true;
      } catch (error) {
          console.error("Update Status Error:", error);
          if(status !== "Dispatched") toast.error("Failed to update status");
          return false; 
      }
  };

  // 🟢 GOODS RECEIPT (Triggers Inventory Update)
  const markAsDelivered = async (poId, warehouseId, bin) => {
      try {
          await api.post(`/purchase/orders/${poId}/receive`, { warehouseId, bin });
          toast.success("Stock Received & Added to Inventory!");
          fetchPOs();
          return true;
      } catch (error) { 
          toast.error("Failed to receive goods"); 
          return false;
      }
  };

  const updatePR = async (id, data) => {
    try {
      await api.put(`/purchase/requests/${id}`, data);
      toast.success("Purchase Request updated successfully");
      fetchPRs();
      return true;
    } catch (err) {
      toast.error("Failed to update Purchase Request");
      return false;
    }
  };

  const updatePO = async (id, data) => {
    try {
      await api.put(`/purchase/orders/${id}`, data);
      toast.success("Purchase Order updated successfully");
      fetchPOs();
      return true;
    } catch (err) {
      toast.error("Failed to update Purchase Order");
      return false;
    }
  };

  const deletePO = async (id) => {
    try {
      await api.delete(`/purchase/orders/${id}`);
      setPurchaseOrders(prev => prev.filter(p => String(p.id) !== String(id)));
      toast.success("Purchase Order deleted");
      return true;
    } catch (err) {
      toast.error("Failed to delete Purchase Order");
      return false;
    }
  };

  return (
    <PurchaseOrderContext.Provider value={{
        purchaseRequests, createPurchaseRequest, updatePR, deletePR, getPR, fetchPRById, updatePRStatus, approvePR,
        quotations, loadQuotations, addQuotation, selectQuotation, getQuotationsForPR, getSelectedQuotation,
        purchaseOrders, createPurchaseOrder, updatePO, deletePO, getPO, updatePOStatus, 
        markAsDelivered,
        getRFQsForVendor, submitQuoteFromRFQ,
        fetchPOById,
        fetchPRs,
        fetchPOs
    }}>
      {children}
    </PurchaseOrderContext.Provider>
  );
};